import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { surveyTemplatesAPI } from '../../services/api';
import { QuestionInput, ResponseType } from '../../components/survey/QuestionInputs';

interface SurveyQuestion {
  id: string;
  question: string;
  responseType: ResponseType;
  required: boolean;
  choices: string[];
  order: number;
}

interface SurveyData {
  id: string;
  name: string;
  description?: string;
  surveyType: string;
  emailId?: string;
  ticketId?: string;
  alreadySubmitted?: boolean;
  completedAt?: string;
  questions: SurveyQuestion[];
}

const PublicSurvey: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();

  const [surveyData, setSurveyData] = useState<SurveyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [emailId, setEmailId] = useState<string | null>(null);
  const [ticketId, setTicketId] = useState<string | null>(null);

  useEffect(() => {
    const fetchSurvey = async () => {
      if (!surveyId) {
        setError('Invalid survey link. Missing survey ID.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // No token needed - Survey ID is used as public reference, validation happens server-side
        const response = await surveyTemplatesAPI.getPublicSurvey(surveyId);
        if (response.data && response.data.success) {
          const data = response.data.data;
          setSurveyData(data);
          
          // Check if survey is already submitted
          if (data.alreadySubmitted === true) {
            setAlreadySubmitted(true);
          }
          
          // Extract emailId and ticketId from backend response (they are embedded in token)
          if (data.emailId && data.ticketId) {
            setEmailId(data.emailId);
            setTicketId(data.ticketId);
          }
        } else {
          setError('Failed to load survey');
        }
      } catch (err: any) {
        console.error('Error fetching survey:', err);
        const errorMessage = err.response?.data?.message || 'Failed to load survey. Invalid or expired link.';
        // Check if it's an expired token error
        if (errorMessage.includes('expired') || errorMessage.includes('Survey link has expired')) {
          setError('This survey link has expired. The survey can no longer be accessed or submitted. Please contact support for a new survey link.');
        } else {
          setError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSurvey();
  }, [surveyId]);

  const handleResponseChange = (questionId: string, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
    // Clear error for this question when user starts answering
    if (errors[questionId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionId];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate and get errors
    const newErrors: Record<string, string> = {};
    if (!surveyData || !surveyId) return;

    surveyData.questions.forEach((question) => {
      if (question.required) {
        const response = responses[question.id];
        if (response === undefined || response === null || response === '') {
          newErrors[question.id] = 'This question is required';
        } else if (Array.isArray(response) && response.length === 0) {
          newErrors[question.id] = 'This question is required';
        }
      }
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Scroll to first error
      const firstErrorId = Object.keys(newErrors)[0];
      if (firstErrorId) {
        setTimeout(() => {
          const element = document.getElementById(`question-${firstErrorId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      return;
    }

    try {
      setLoading(true);
      // Submit survey responses to API (no token needed - Survey ID is used, validation happens server-side)
      await surveyTemplatesAPI.submitSurvey(surveyId!, responses);
      setSubmitted(true);
    } catch (err: any) {
      console.error('Error submitting survey:', err);
      const errorMessage = err.response?.data?.message || 'Failed to submit survey. Please try again.';
      
      // Check if it's an expired token error
      if (errorMessage.includes('expired') || errorMessage.includes('Survey link has expired')) {
        setError('This survey link has expired. The survey can no longer be accessed or submitted. Please contact support for a new survey link.');
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  // Sort questions by order
  const sortedQuestions = surveyData?.questions 
    ? [...surveyData.questions].sort((a, b) => a.order - b.order)
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Check for already submitted FIRST, before error check
  // Check both state variable and data property to ensure we catch it
  const isAlreadySubmitted = alreadySubmitted || (surveyData?.alreadySubmitted === true);
  
  if (isAlreadySubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-yellow-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Survey Already Submitted
          </h2>
          <p className="text-gray-600 mb-4">This survey has already been submitted.</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
            <p className="text-sm text-yellow-800">
              <strong>Note:</strong> This survey has already been submitted and cannot be modified or resubmitted.
              {surveyData?.completedAt && (
                <> It was submitted on {new Date(surveyData.completedAt).toLocaleString()}.</>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !surveyData) {
    const isExpired = error?.includes('expired') || error?.includes('Survey link has expired');
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className={`${isExpired ? 'text-yellow-600' : 'text-red-600'} mb-4`}>
            {isExpired ? (
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            )}
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isExpired ? 'Survey Link Expired' : 'Access Denied'}
          </h2>
          <p className="text-gray-600 mb-4">{error || 'Survey not found'}</p>
          {isExpired && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-left">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Survey links are valid for 7 days from the time they are generated. 
                After expiration, the survey cannot be accessed or submitted.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <div className="text-green-600 mb-4">
            <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-4">Your survey response has been received.</p>
          <p className="text-sm text-gray-500">
            (Note: In Phase 1, responses are logged to console only)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* Survey Header */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{surveyData.name}</h1>
            {surveyData.description && (
              <p className="text-gray-600">{surveyData.description}</p>
            )}
          </div>

          {/* Survey Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {sortedQuestions.map((question) => (
              <div 
                key={question.id} 
                id={`question-${question.id}`}
                className="space-y-3"
              >
                <label className="block text-sm font-medium text-gray-700">
                  {question.question}
                  {question.required && (
                    <span className="text-red-500 ml-1">*</span>
                  )}
                </label>
                <QuestionInput
                  responseType={question.responseType}
                  value={responses[question.id]}
                  onChange={(value) => handleResponseChange(question.id, value)}
                  disabled={false}
                  choices={question.choices}
                  required={question.required}
                  questionId={question.id}
                />
                {errors[question.id] && (
                  <p className="text-sm text-red-600 mt-1">{errors[question.id]}</p>
                )}
              </div>
            ))}

            {/* Submit Button */}
            <div className="pt-6 border-t border-gray-200">
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-blue-600 text-white rounded-md text-base font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Submitting...' : 'Submit Survey'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PublicSurvey;

